import { useEffect, useState } from "react";
import {
  INTERVALS,
  intervalLabel,
  isUnlimited,
  type AdminOrgDetail,
  type PlanInterval,
  type PlanUsageInfo,
} from "@currencycore/shared";
import { downgradeOrg, listOrgPlans, setOrgPlan } from "../lib/api.ts";
import { Select } from "./Select.tsx";
import { useConfirm } from "./useConfirm.tsx";
import { toast } from "./Toast.tsx";
import { errMsg } from "../lib/ui.ts";
import { compact } from "./Charts.tsx";

export function PlanEditor({
  detail,
  onUpdated,
}: {
  detail: AdminOrgDetail;
  onUpdated: (d: AdminOrgDetail) => void;
}) {
  const sub = detail.subscription;
  const [plans, setPlans] = useState<PlanUsageInfo[]>([]);
  const [planId, setPlanId] = useState(sub.planId);
  const [interval, setIntervalSel] = useState<PlanInterval | "">(sub.billingInterval ?? "");
  const { ask, dialog } = useConfirm();

  useEffect(() => {
    listOrgPlans(detail.id)
      .then((ps) => setPlans(ps.filter((p) => !p.archived || p.id === sub.planId)))
      .catch(() => {});
  }, [detail.id, sub.planId]);

  const selected = plans.find((p) => p.id === planId);
  const intervals = selected ? INTERVALS.filter((i) => (selected.prices[i] ?? 0) > 0) : [];
  const isPaid = intervals.length > 0;
  const samePlan = planId === sub.planId;
  const intervalLocked = samePlan && !!sub.billingInterval;

  const planOptions = plans.map((p) => ({
    value: p.id,
    label: p.kind === "custom" ? `${p.name} (custom)` : p.name,
  }));
  const intervalOptions = intervals.map((i) => ({ value: i, label: intervalLabel(i) }));

  function pickPlan(v: string) {
    setPlanId(v);
    if (v === sub.planId) {
      setIntervalSel(sub.billingInterval ?? "");
    } else {
      const p = plans.find((x) => x.id === v);
      const ivs = p ? INTERVALS.filter((i) => (p.prices[i] ?? 0) > 0) : [];
      setIntervalSel(ivs[0] ?? "");
    }
  }

  async function doSave() {
    try {
      const d = await setOrgPlan(detail.id, {
        planId,
        ...(interval ? { billingInterval: interval as PlanInterval } : {}),
      });
      onUpdated(d);
      toast("Plan assigned.", true);
    } catch (e) {
      toast(errMsg(e, "Could not assign the plan."));
    }
  }

  function confirmSave() {
    if (isPaid && !intervalLocked && !interval) {
      toast("Choose a billing interval.");
      return;
    }
    ask({
      title: "Assign plan?",
      message: (
        <>
          Assign <strong>{selected?.name ?? planId}</strong>
          {isPaid && interval ? <> billed <strong>{intervalLabel(interval as PlanInterval).toLowerCase()}</strong></> : ""} to{" "}
          <strong>{detail.name}</strong>? This changes the org's quota and rate limit.
        </>
      ),
      confirmLabel: "Assign plan",
      run: doSave,
    });
  }

  async function doDowngrade() {
    try {
      const d = await downgradeOrg(detail.id);
      onUpdated(d);
      toast("Downgrade to Hobby scheduled for period end.", true);
    } catch (e) {
      toast(errMsg(e, "Could not schedule the downgrade."));
    }
  }

  function confirmDowngrade() {
    ask({
      title: "Downgrade to Hobby?",
      message: (
        <>
          Schedule <strong>{detail.name}</strong> to move to <strong>Hobby</strong> at the end of its current
          billing period. It keeps its plan and live keys until then; afterwards live keys stop working (test
          keys keep working at the Hobby limit). Stops Dodo billing at the next renewal.
        </>
      ),
      confirmLabel: "Schedule downgrade",
      run: doDowngrade,
    });
  }

  return (
    <section className="drawer-section">
      <h4>Plan &amp; billing</h4>
      <div className="field">
        <label className="form-label">Plan</label>
        <Select value={planId} options={planOptions} onChange={pickPlan} ariaLabel="Plan" />
      </div>
      {isPaid && (
        <div className="field">
          <label className="form-label">
            Billing interval {intervalLocked && <span className="muted">(locked)</span>}
          </label>
          {intervalLocked ? (
            <div className="locked-field">
              {intervalLabel(sub.billingInterval as PlanInterval)} (locked once assigned)
            </div>
          ) : (
            <Select value={interval} options={intervalOptions} onChange={(v) => setIntervalSel(v as PlanInterval)} ariaLabel="Billing interval" />
          )}
        </div>
      )}
      <p className="hint">
        {selected ? (isUnlimited(selected.monthlyRequests) ? "Unlimited" : compact(selected.monthlyRequests!)) : "—"} requests/mo
        {selected?.rateLimit ? ` · ${selected.rateLimit} req/window` : ""}
      </p>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <button className="btn btn-primary btn-sm" onClick={confirmSave}>Save plan</button>
        {sub.planId !== "free" && (
          <button className="btn btn-ghost btn-sm" onClick={confirmDowngrade}>Downgrade to Hobby</button>
        )}
      </div>
      {dialog}
    </section>
  );
}
